import React, { useState } from 'react';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { getLanguage } from '../utils/language';

function categoryIdFromAITreeResponse(data: unknown): number | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const o = data as Record<string, unknown>;
  if (typeof o.category_id === 'number') return o.category_id;
  if (typeof o.id === 'number') return o.id;
  const cat = o.category;
  if (cat && typeof cat === 'object' && cat !== null && 'id' in cat) {
    const id = (cat as { id: unknown }).id;
    if (typeof id === 'number') return id;
  }
  return undefined;
}

type Mode = 'free' | 'fromtext';

type WordType =
  | 'random'
  | 'advanced'
  | 'idioms'
  | 'phrasal_verbs'
  | 'verbs'
  | 'nouns'
  | 'adjectives'
  | 'adverbs';

const WORD_TYPES: { label: string; value: WordType }[] = [
  { label: 'Random', value: 'random' },
  { label: 'Advanced', value: 'advanced' },
  { label: 'Idioms', value: 'idioms' },
  { label: 'Phrasal Verbs', value: 'phrasal_verbs' },
  { label: 'Verbs', value: 'verbs' },
  { label: 'Nouns', value: 'nouns' },
  { label: 'Adjectives', value: 'adjectives' },
  { label: 'Adverbs', value: 'adverbs' },
];

interface AITreeGeneratorProps {
  onClose: () => void;
  onSuccess: (newCategoryId?: number) => void | Promise<void>;
  instanceId: number | null;
  userEmail?: string;
}

export const AITreeGenerator: React.FC<AITreeGeneratorProps> = ({ onClose, onSuccess, instanceId, userEmail }) => {
  const [mode, setMode] = useState<Mode>('free');
  const [prompt, setPrompt] = useState('');
  const [fromText, setFromText] = useState('');
  const [wordType, setWordType] = useState<WordType>('random');
  const [cardsOnly, setCardsOnly] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubmitDisabled =
    isGenerating ||
    (mode === 'free' && !prompt.trim()) ||
    (mode === 'fromtext' && !fromText.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!instanceId) { setError('Instance ID is required'); return; }
    setIsGenerating(true);
    setError(null);

    const language = getLanguage(userEmail ?? '');

    try {
      let result: unknown;

      if (mode === 'free') {
        result = await apiClient.generateAITree({
          language,
          instance_id: instanceId,
          prompt: prompt.trim(),
          ...(cardsOnly && { cards_only: true }),
        });
      } else {
        result = await apiClient.generateFromText({
          text: fromText.trim(),
          word_type: wordType,
          language,
          instance_id: instanceId,
        });
      }

      const newCategoryId = categoryIdFromAITreeResponse(result);
      setIsGenerating(false);
      await Promise.resolve(onSuccess(newCategoryId));
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate tree structure');
      console.error('Error generating AI tree:', err);
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (isGenerating) {
      const confirmed = window.confirm('AI generation is in progress. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onClose();
  };

  const handleModeChange = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const segmentClass = (m: Mode) =>
    m === mode
      ? 'flex-1 px-2 py-2 text-sm font-semibold rounded-md bg-purple-600 text-white transition-colors text-center'
      : 'flex-1 px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-100 transition-colors text-center';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Generate vocabulary with AI</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Loading Overlay */}
        {isGenerating && (
          <div className="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-10 rounded-lg">
            <Loader2 className="h-12 w-12 text-purple-600 animate-spin mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              AI is generating your category tree...
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              This may take a few seconds. Our AI is creating a complete category structure with folders and vocabulary cards based on your input.
            </p>
            <div className="mt-6 flex space-x-2">
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Mode selector */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-full">
              <button type="button" onClick={() => handleModeChange('free')} className={segmentClass('free')}>
                Free Prompt
              </button>
              <button type="button" onClick={() => handleModeChange('fromtext')} className={segmentClass('fromtext')}>
                From Text
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Free Prompt */}
            {mode === 'free' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., A gym vocabulary tree: folders for Workouts and Useful Expressions, with subfolders for Legs, Arms, and Chest..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  disabled={isGenerating}
                  autoFocus
                />
                <label className="mt-3 flex items-center gap-2 cursor-pointer select-none w-fit">
                  <input
                    type="checkbox"
                    checked={cardsOnly}
                    onChange={(e) => setCardsOnly(e.target.checked)}
                    disabled={isGenerating}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Cards only (no folders)</span>
                </label>
              </div>
            )}

            {/* From Text */}
            {mode === 'fromtext' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source text <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={fromText}
                    onChange={(e) => setFromText(e.target.value)}
                    placeholder="Paste or type any text here — articles, notes, dialogue..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    style={{ minHeight: '200px' }}
                    disabled={isGenerating}
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Word type</label>
                  <div className="flex flex-wrap gap-2">
                    {WORD_TYPES.map(({ label, value }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setWordType(value)}
                        disabled={isGenerating}
                        className={
                          wordType === value
                            ? 'px-3 py-1.5 text-sm font-semibold rounded-full bg-purple-600 text-white transition-colors'
                            : 'px-3 py-1.5 text-sm font-medium rounded-full border border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-700 transition-colors'
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isGenerating}
            className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-5 py-2.5 text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <Sparkles className="h-4 w-4" />
            <span>{isGenerating ? 'Generating...' : 'Generate vocabulary'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
